class FacetFiltersForm extends HTMLElement {
	constructor() {
		super();
		this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

		this.debouncedOnSubmit = debounce((event) => {
			this.onSubmitHandler(event);
		}, 500);

		const facetForm = this.querySelector('form');
		facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
	}

	static setListeners() {
		const onHistoryChange = (event) => {
			const searchParams = event.state
				? event.state.searchParams
				: FacetFiltersForm.searchParamsInitial;
			if (searchParams === FacetFiltersForm.searchParamsPrev) return;
			FacetFiltersForm.renderPage(searchParams, null, false);
		};
		window.addEventListener('popstate', onHistoryChange);
	}

	static toggleActiveFacets(disable = true) {
		document.querySelectorAll('[data-facets-remove]').forEach((element) => {
			element.classList.toggle('disabled', disable);
		});
	}

	static renderPage(searchParams, event, updateURLHash = true) {
		FacetFiltersForm.searchParamsPrev = searchParams;
		const sections = FacetFiltersForm.getSections();

		sections.forEach((section) => {
			const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
			const filterDataUrl = (element) => element.url === url;

			FacetFiltersForm.filterData.some(filterDataUrl)
				? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
				: FacetFiltersForm.renderSectionFromFetch(url, event);
		});

		if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
	}

	static renderSectionFromFetch(url, event) {
		fetch(url)
			.then((response) => response.text())
			.then((responseText) => {
				const html = responseText;
				FacetFiltersForm.filterData = [
					...FacetFiltersForm.filterData,
					{html, url},
				];
				FacetFiltersForm.renderFilters(html, event);
				FacetFiltersForm.renderProductGridContainer(html);
				FacetFiltersForm.renderProductCount(html);
			});
	}

	static renderSectionFromCache(filterDataUrl, event) {
		const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
		FacetFiltersForm.renderFilters(html, event);
		FacetFiltersForm.renderProductGridContainer(html);
		FacetFiltersForm.renderProductCount(html);
	}

	static renderProductGridContainer(html) {
		const container = document.querySelector('[data-facets-products-grid]');

		container.innerHTML = new DOMParser()
			.parseFromString(html, 'text/html')
			.querySelector('[data-facets-products-grid]').innerHTML;

		// Dispatch event to revive islands (load more)
		document.dispatchEvent(
			new CustomEvent('update-grid-collection', {
				detail: {container},
			}),
		);

		// Dispatch event for datalayer re-init
		document.dispatchEvent(new CustomEvent('facets:rendered'));
	}

	static renderProductCount(html) {
		const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

		// [data-facets-count] holds the raw count, [data-facets-count-label] the
		// full sentence ("59 products") displayed in the facets bar.
		['[data-facets-count]', '[data-facets-count-label]'].forEach((selector) => {
			const source = parsedHTML.querySelector(selector);
			if (!source) return;

			document.querySelectorAll(selector).forEach((container) => {
				container.innerHTML = source.innerHTML;
			});
		});
	}

	static renderFilters(html, event) {
		const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

		const facetDetailsElements = parsedHTML.querySelectorAll(
			'[data-facets-form] [data-facets-filter]',
		);
		const matchesIndex = (element) => {
			const jsFilter = event
				? event.target.closest('[data-facets-filter]')
				: undefined;
			return jsFilter
				? element.dataset.index === jsFilter.dataset.index
				: false;
		};
		const facetsToRender = Array.from(facetDetailsElements).filter(
			(element) => !matchesIndex(element),
		);
		facetsToRender.forEach((element) => {
			document.querySelector(
				`[data-index="${element.dataset.index}"]`,
			).innerHTML = element.innerHTML;
		});

		FacetFiltersForm.renderActiveFacets(parsedHTML);
	}

	static renderActiveFacets(html) {
		const activeFacetElementSelectors = ['[data-facets-actives]'];

		activeFacetElementSelectors.forEach((selector) => {
			const activeFacetsElement = html.querySelector(selector);
			if (!activeFacetsElement) return;
			document.querySelector(selector).innerHTML =
				activeFacetsElement.innerHTML;
		});

		FacetFiltersForm.toggleActiveFacets(false);
	}

	static updateURLHash(searchParams) {
		history.pushState(
			{searchParams},
			'',
			`${window.location.pathname}${searchParams && '?'.concat(searchParams)}`,
		);
	}

	static getSections() {
		return [
			{
				section: document.querySelector('[data-facets-product-grid]').dataset
					.id,
			},
		];
	}

	createSearchParams(form) {
		const formData = new FormData(form);
		const searchParams = new URLSearchParams(formData);
		const hasVariantOrPriceFilter =
			searchParams.get('filter.v.option') ||
			searchParams.get('filter.p.m') ||
			searchParams.get('filter.v.m') ||
			searchParams.get('filter.p.vendor') ||
			searchParams.get('filter.v.price.gte') ||
			searchParams.get('filter.v.price.lte');

		const hasAvailabilityFilter = searchParams.has('filter.v.availability');

		if (hasVariantOrPriceFilter && !hasAvailabilityFilter) {
			searchParams.append('filter.v.availability', '1');
		} else if (!hasVariantOrPriceFilter && hasAvailabilityFilter) {
			searchParams.delete('filter.v.availability');
		}

		return searchParams.toString();
	}

	onSubmitForm(searchParams, event) {
		FacetFiltersForm.renderPage(searchParams, event);
	}

	onSubmitHandler(event) {
		event.preventDefault();
		const sortFilterForms = document.querySelectorAll(
			'facet-filters-form form',
		);
		const forms = [];

		sortFilterForms.forEach((form) => {
			// FacetFiltersForm: filters panel / FacetSortForm: sort by dropdown
			if (form.id === 'FacetFiltersForm' || form.id === 'FacetSortForm') {
				forms.push(this.createSearchParams(form));
			}
		});
		this.onSubmitForm(forms.join('&'), event);
	}

	onActiveFilterClick(event) {
		event.preventDefault();
		FacetFiltersForm.toggleActiveFacets();
		const url =
			event.currentTarget.href.indexOf('?') == -1
				? ''
				: event.currentTarget.href.slice(
						event.currentTarget.href.indexOf('?') + 1,
					);
		FacetFiltersForm.renderPage(url);
	}
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
if (!customElements.get('facet-filters-form')) {
	customElements.define('facet-filters-form', FacetFiltersForm);
}
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
	constructor() {
		super();
		this.querySelectorAll('input[type="range"]').forEach((element) =>
			element.addEventListener('input', this.onRangeChange.bind(this)),
		);
		this.setMinAndMaxValues();
	}

	onRangeChange(event) {
		const minInput = this.querySelector('[data-price-range-min]');
		const maxInput = this.querySelector('[data-price-range-max]');

		// Limiter la plage de prix à 50€
		const min = parseFloat(minInput.value);
		const max = parseFloat(maxInput.value);
		const diffMinMax = 1;
		if (max - min < diffMinMax) {
			if (event.currentTarget === minInput) {
				maxInput.value = min + diffMinMax;
			} else {
				minInput.value = max - diffMinMax;
			}
		}

		const realInputMin = this.querySelector('input[name="filter.v.price.gte"]');
		const realInputMax = this.querySelector('input[name="filter.v.price.lte"]');

		realInputMin.value = minInput.value;
		realInputMax.value = maxInput.value;

		this.adjustToValidValues(event.currentTarget);
		this.setMinAndMaxValues();
		this.updateTrackColors(); // Appel pour mettre à jour les couleurs
	}

	setMinAndMaxValues() {
		const minActiveValueElement = this.querySelector('[data-min-active-value]');
		const maxActiveValueElement = this.querySelector('[data-max-active-value]');
		const currency = this.getAttribute('data-currency');
		const inputs = this.querySelectorAll('input[type="range"]');
		const minInput = inputs[0];
		const maxInput = inputs[1];

		if (minInput.value)
			minActiveValueElement.textContent = `${minInput.value}${currency}`;
		if (maxInput.value)
			maxActiveValueElement.textContent = `${maxInput.value}${currency}`;

		this.updateTrackColors(); // Appel pour mettre à jour les couleurs
	}

	adjustToValidValues(input) {
		const value = parseFloat(input.value);
		const min = parseFloat(input.getAttribute('min'));
		const max = parseFloat(input.getAttribute('max'));

		if (value < min) input.value = min;
		if (value > max) input.value = max;
	}

	updateTrackColors() {
		const minInput = this.querySelector('[data-price-range-min]');
		const maxInput = this.querySelector('[data-price-range-max]');
		const min = parseFloat(minInput.value);
		const max = parseFloat(maxInput.value);
		const rangeMin = parseFloat(minInput.getAttribute('min'));
		const rangeMax = parseFloat(maxInput.getAttribute('max'));

		const minPercentage = ((min - rangeMin) / (rangeMax - rangeMin)) * 100;
		const maxPercentage = ((max - rangeMin) / (rangeMax - rangeMin)) * 100;

		minInput.style.background = `linear-gradient(to right, 
      #d9d9d9 ${minPercentage}%, 
      #000000 ${minPercentage}%, 
      #000000 ${maxPercentage}%, 
      #d9d9d9 ${maxPercentage}%)`;
		maxInput.style.background = minInput.style.background;
	}
}

if (!customElements.get('price-range')) {
	customElements.define('price-range', PriceRange);
}

class FacetRemove extends HTMLElement {
	constructor() {
		super();
		const facetLink = this.querySelector('a');
		facetLink.setAttribute('role', 'button');
		facetLink.addEventListener('click', this.closeFilter.bind(this));
		facetLink.addEventListener('keyup', (event) => {
			if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
		});
	}

	closeFilter(event) {
		event.preventDefault();
		const form = this.closest('facet-filters-form');
		form.onActiveFilterClick(event);
	}
}

if (!customElements.get('facet-remove')) {
	customElements.define('facet-remove', FacetRemove);
}
